import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Headphones, Play, Pause, Plus, Check, Calendar, Clock } from 'lucide-react';
import Layout from '../components/Layout';
import ContentRow from '../components/ContentRow';
import ContentCard, { OriginalBadge } from '../components/ContentCard';
import { PageHeader } from '../components/PageBits';
import { useCatalog } from '../context/CatalogContext';
import { useApp, fmtDuration, fmtNum } from '../context/AppContext';
import { toast } from 'sonner';


export function EpisodeCard({ podcast, ep }) {
  const { audio, playEpisode } = useApp();
  const active = audio.episode?.id === ep.id;
  return (
    <div className="flex-none w-[300px] elevated p-3 flex gap-3 hover:bg-white/[0.04] transition-colors cursor-pointer group" onClick={() => playEpisode(podcast.id, ep)} data-testid={`ep-card-${ep.id}`}>
      <div className="relative w-[90px] h-[90px] rounded-lg overflow-hidden flex-none">
        <img src={ep.image} alt="" className="w-full h-full object-cover" />
        <div className={`absolute inset-0 bg-black/40 flex items-center justify-center ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
          <span className="w-9 h-9 rounded-full bg-white text-black flex items-center justify-center">{active && audio.playing ? <Pause size={15} fill="currentColor" /> : <Play size={15} fill="currentColor" className="ml-0.5" />}</span>
        </div>
      </div>
      <div className="min-w-0 flex flex-col">
        <div className="text-[11px] text-[#8B2CFF] font-semibold truncate">{podcast.title}</div>
        <div className="text-[13px] font-semibold leading-snug line-clamp-2 mt-0.5">{ep.title}</div>
        <div className="mt-auto flex items-center gap-3 text-[11px] text-[#8A8694]"><span className="flex items-center gap-1"><Clock size={11} />{fmtDuration(ep.duration)}</span><span className="flex items-center gap-1"><Calendar size={11} />{new Date(ep.publishedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span></div>
      </div>
    </div>
  );
}

function FeaturedPodcast({ p }) {
  const navigate = useNavigate();
  const { following, toggleFollow, playEpisode, audio } = useApp();
  const isF = following.includes(p.id);
  return (
    <section className="relative mx-[22px] rounded-xl overflow-hidden" style={{ height: 'clamp(260px, 22vw, 340px)' }} data-testid="podcast-hero">
      <img src={p.banner} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(20,6,40,0.96) 0%, rgba(20,6,40,0.75) 40%, rgba(20,6,40,0.15) 75%)' }} />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(6,9,15,0.9), transparent 45%)' }} />
      <div className="absolute left-8 bottom-7 right-8 flex items-end gap-6">
        <img src={p.image} alt="" className="w-[150px] h-[150px] rounded-xl object-cover shadow-2xl hidden md:block ring-1 ring-black/5" />
        <div className="min-w-0">
          <OriginalBadge kind="PODCAST" className="!text-[11px] [&_img]:!h-[11px]" />
          <h2 className="text-[36px] font-extrabold tracking-tight mt-1 leading-none">{p.title}</h2>
          <div className="text-[14px] text-[#C7C4CB] mt-2 flex items-center gap-2"><img src={p.hostAvatar} alt="" className="w-6 h-6 rounded-full object-cover" />{p.host} &bull; {fmtNum(p.followers)} followers &bull; {p.episodes.length} episodes</div>
          <p className="text-[13.5px] text-white/85 mt-2 max-w-[520px] line-clamp-2">{p.description}</p>
          <div className="mt-4 flex items-center gap-3">
            <button className="btn-play" onClick={() => { playEpisode(p.id, p.episodes[0]); }} data-testid="podcast-hero-play">{audio.episode?.id === p.episodes[0].id && audio.playing ? <Pause size={16} fill="currentColor" /> : <Headphones size={16} />} Play Latest</button>
            <button className={`btn-ghost ${isF ? '!border-[#8B2CFF] !bg-[#8B2CFF]/15' : ''}`} onClick={() => { toggleFollow(p.id); toast.success(isF ? 'Unfollowed' : `Following ${p.title}`); }} data-testid="podcast-hero-follow">{isF ? <Check size={16} /> : <Plus size={16} />} {isF ? 'Following' : 'Follow'}</button>
            <button className="btn-ghost" onClick={() => navigate(`/podcasts/${p.id}`)}>All Episodes</button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Podcasts() {
  const { podcasts } = useCatalog();
  const byGenre = (g) => podcasts.filter((p) => p.genres.includes(g));
  const newEps = podcasts.flatMap((p) => p.episodes.slice(0, 1).map((ep) => ({ p, ep }))).sort((a, b) => new Date(b.ep.publishedAt) - new Date(a.ep.publishedAt));
  const sections = [
    ['Trending Podcasts', [...podcasts].sort((a, b) => b.followers - a.followers)],
    ['African Voices', podcasts.filter((p) => /African|Lagos|Voices|Boardroom|Culture/.test(p.title))],
    ['Business', byGenre('Business')], ['Culture', byGenre('Culture')], ['Entertainment', byGenre('Entertainment')],
    ['True Stories', byGenre('True Stories')], ['Comedy', byGenre('Comedy')], ['Technology', byGenre('Technology')], ['Society', byGenre('Society')],
  ];
  return (
    <Layout>
      <PageHeader title="Podcasts" subtitle="Fresh voices, inspiring talks and untold stories." />
      {podcasts[0] && <FeaturedPodcast p={podcasts[0]} />}
      <ContentRow title="New Episodes" items={newEps} testId="podcasts-row-new-episodes">
        {newEps.map(({ p, ep }) => <EpisodeCard key={ep.id} podcast={p} ep={ep} />)}
      </ContentRow>
      {sections.map(([t, items]) => <ContentRow key={t} title={t} items={items} testId={`podcasts-row-${t.toLowerCase().replace(/\s+/g, '-')}`} />)}
      <div className="h-10" />
    </Layout>
  );
}
